import type {
  CampaignContent,
  CampaignContentOverride,
  CampaignVariant,
  GuideCampaignConfig,
  ResolvedCampaignContent,
} from './types';
import { getCampaignDelayMs, shouldForceGuideRecruitmentCampaign } from './guide-recruitment.campaign';

function mergeContent(base: CampaignContent, override?: CampaignContentOverride): CampaignContent {
  if (!override) return base;
  return {
    ...base,
    ...override,
    benefits: override.benefits ?? base.benefits,
  };
}

function isWithinDateWindow(config: GuideCampaignConfig, now = new Date()): boolean {
  if (config.startDate) {
    const start = new Date(config.startDate);
    if (now < start) return false;
  }
  if (config.endDate) {
    const end = new Date(config.endDate);
    end.setHours(23, 59, 59, 999);
    if (now > end) return false;
  }
  return true;
}

function normalizeCity(city: string | null | undefined): string | undefined {
  if (!city?.trim()) return undefined;
  return city.trim();
}

function matchCityOverride(
  overrides: Record<string, CampaignContentOverride> | undefined,
  city: string | undefined,
): CampaignContentOverride | undefined {
  if (!overrides || !city) return undefined;
  const exact = overrides[city];
  if (exact) return exact;
  const lower = city.toLowerCase();
  const entry = Object.entries(overrides).find(([key]) => key.toLowerCase() === lower);
  return entry?.[1];
}

export function isCampaignActive(config: GuideCampaignConfig, now = new Date()): boolean {
  if (shouldForceGuideRecruitmentCampaign()) return true;
  return config.enabled && isWithinDateWindow(config, now);
}

export function resolveCampaignDelayMs(config: GuideCampaignConfig): number {
  return getCampaignDelayMs(config);
}

export function resolveCampaignContent(
  config: GuideCampaignConfig,
  options: {
    variant: CampaignVariant;
    selectedCity?: string | null;
  },
): ResolvedCampaignContent {
  const city = normalizeCity(options.selectedCity);
  const base =
    options.variant === 'continue'
      ? mergeContent(config.default, config.continueVariant)
      : config.default;

  const cityOverride =
    options.variant === 'recruit' ? matchCityOverride(config.cityOverrides, city) : undefined;

  const merged = mergeContent(base, cityOverride);

  return {
    ...merged,
    campaignId: config.id,
    variant: options.variant,
    selectedCity: city,
  };
}
